/**
 * useCompass — "walk-to-pin" compass overlay state + lifecycle.
 *
 * When the user activates compass mode from a detail panel, this hook
 * starts a continuous geolocation watch and a deviceorientation
 * listener so the overlay can point an arrow from the user toward the
 * selected pin. Auto-closes when the pin is deselected.
 *
 * Platform quirks handled:
 *   - iOS Safari: webkitCompassHeading (degrees from north, clockwise).
 *   - Android Chrome: deviceorientationabsolute event + alpha inversion.
 *   - iOS requestPermission gate before orientation events fire.
 *
 * Inputs: the currently-selected pin (any layer type) and the user
 * location lat/lng (already resolved by useGeo / handleLocate).
 *
 * Consumers: App.tsx (CompassOverlay render gate + activate buttons in
 * DetailSheetWrapper and FolkloreDetail).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FolkloreRecord, Lang, LayerType } from "../types/index.ts";
import { t } from "../i18n/index.ts";
import { headingFromOrientation } from "../utils/heading.ts";
import { requestCompassPermission } from "../utils/compassPermission.ts";

export interface CompassTarget {
  name: string;
  lat: number;
  lng: number;
}

export interface UseCompassParams {
  // Detail records are heterogeneous across layers; the hook only reads
  // lat/lng/name fields if present.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selected: any;
  selectedType: LayerType;
  lang: Lang;
  userLocation: [number, number] | null;
  setUserLocation: (loc: [number, number]) => void;
  // Called when the user activates compass before any location is known —
  // typically the existing handleLocate that asks for one-shot geolocation.
  handleLocate: () => void;
}

export interface UseCompassReturn {
  compassActive: boolean;
  compassHeading: number | null;
  compassUserPos: [number, number] | null;
  compassTarget: CompassTarget | null;
  handleCompassActivate: () => void;
  closeCompass: () => void;
}

export function useCompass({
  selected, selectedType, lang, userLocation, setUserLocation, handleLocate,
}: UseCompassParams): UseCompassReturn {
  const [compassActive, setCompassActive] = useState(false);
  const [compassHeading, setCompassHeading] = useState<number | null>(null);
  const [compassUserPos, setCompassUserPos] = useState<[number, number] | null>(null);
  const compassWatchId = useRef<number | null>(null);

  const compassTarget = useMemo<CompassTarget | null>(() => {
    if (!selected) return null;
    if (selectedType === "folklore") {
      const recs = selected as FolkloreRecord[];
      if (!recs.length) return null;
      return { name: recs[0].title || recs[0].placeName || recs[0].name, lat: recs[0].lat, lng: recs[0].lng };
    }
    const s = selected as { lat?: number; lng?: number; name?: string; title?: string; label?: string };
    if (typeof s.lat !== "number" || typeof s.lng !== "number") return null;
    return { name: s.name || s.title || s.label || t(`tab.${selectedType}`, lang), lat: s.lat, lng: s.lng };
  }, [selected, selectedType, lang]);

  useEffect(() => {
    if (!compassActive) {
      if (compassWatchId.current !== null) {
        navigator.geolocation.clearWatch(compassWatchId.current);
        compassWatchId.current = null;
      }
      return;
    }
    compassWatchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setCompassUserPos([pos.coords.longitude, pos.coords.latitude]);
        if (!userLocation) setUserLocation([pos.coords.longitude, pos.coords.latitude]);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 1000 },
    );
    // Device orientation listener — one event per platform to avoid overwrites.
    const onOrientation = (e: DeviceOrientationEvent) => {
      // iOS: webkitCompassHeading = degrees from north, clockwise (Safari
      // extension, not in lib.dom).
      const h = headingFromOrientation(e);
      if (h != null) setCompassHeading(h);
    };
    // Android Chrome: deviceorientationabsolute gives true compass heading.
    // iOS Safari: only deviceorientation exists (with webkitCompassHeading).
    // Only listen to ONE event to avoid non-absolute overwriting absolute.
    const eventName = "ondeviceorientationabsolute" in window
      ? "deviceorientationabsolute" : "deviceorientation";
    window.addEventListener(eventName, onOrientation as EventListener, true);
    return () => {
      if (compassWatchId.current !== null) {
        navigator.geolocation.clearWatch(compassWatchId.current);
        compassWatchId.current = null;
      }
      window.removeEventListener(eventName, onOrientation as EventListener, true);
    };
  }, [compassActive, userLocation, setUserLocation]);

  // Close compass when pin is deselected.
  useEffect(() => {
    if (!selected) setCompassActive(false);
  }, [selected]);

  const handleCompassActivate = useCallback(() => {
    if (!userLocation) {
      handleLocate();
      return;
    }
    setCompassUserPos(userLocation);
    // Shared with Explore Mode, so someone who already granted there is not
    // asked a second time. Activates either way: a refusal still leaves the
    // arrow useful, it just cannot rotate with the device.
    void requestCompassPermission().then(() => setCompassActive(true));
  }, [userLocation, handleLocate]);

  const closeCompass = useCallback(() => setCompassActive(false), []);

  return { compassActive, compassHeading, compassUserPos, compassTarget, handleCompassActivate, closeCompass };
}
